import { Store } from "lucide-react";
import { useMemo } from "react";
import type { ReactNode } from "react";

import { useGetStoresOptionsQuery } from "@/services/store/select-options";

import { SelectInput } from "components/Select";
import { getSelectClasses } from "@/utils/filter-styles";

import { useStoreAccessFilterActions } from "../../filter/hooks/actions";
import { useStoreAccessFilterState } from "../../filter/hooks/filterState";

import { StoreOption } from "./types";

const LABEL_ID = "store-context-header-label";

/**
 * Trocar de loja dispara `setStoreCode`, que reseta filtros de AMBAS as tabs
 * atomicamente.
 */
export default function StoreContextHeader() {
  const { storeCode, storeHealth } = useStoreAccessFilterState();
  const { updateStoreCode } = useStoreAccessFilterActions();

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const storeOptionsWithHealth = useMemo<StoreOption[]>(() => {
    if (!storeOptions) return [];
    return storeOptions.map(opt => {
      const health = storeHealth?.[Number(opt.value)];
      return health ? { ...opt, healthStatus: health.status } : opt;
    });
  }, [storeOptions, storeHealth]);

  const selectedStore: StoreOption | null = storeCode
    ? (storeOptionsWithHealth.find(opt => Number(opt.value) === storeCode) ??
      null)
    : null;

  const formatStoreLabel = (option: unknown): ReactNode => {
    const { healthStatus, label } = option as StoreOption;
    if (!healthStatus) return label;
    const isError = healthStatus === "error";
    return (
      <span className="flex items-center gap-1.5">
        <span
          className={`inline-block h-2 w-2 rounded-full shrink-0 ${
            isError ? "bg-red-500" : "bg-emerald-500"
          }`}
        />
        <span>{label}</span>
      </span>
    );
  };

  const handleStoreChange = (option: unknown) => {
    const opt = option as StoreOption | null;
    updateStoreCode(opt?.value ? Number(opt.value) : null);
  };

  return (
    <div
      data-testid="store-context-header"
      className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3"
    >
      <div
        id={LABEL_ID}
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground shrink-0"
      >
        <Store className="h-4 w-4" aria-hidden="true" />
        <span>Loja selecionada:</span>
      </div>
      <div className="flex-1 max-w-md">
        <SelectInput
          options={storeOptionsWithHealth}
          placeholder="Selecione uma loja"
          value={selectedStore}
          onChange={handleStoreChange}
          formatOptionLabel={formatStoreLabel}
          className={getSelectClasses(!!storeCode)}
          isClearable={false}
          aria-labelledby={LABEL_ID}
        />
      </div>
    </div>
  );
}
